/**
 * The console's client for its own API.
 *
 * Same contract as the panel's: errors carry a `message_key` and the client
 * translates it, so the server never has to know which language the operator
 * reads in.
 */

import { type Locale, translate } from "@/i18n";

export class ApiError extends Error {
	constructor(
		readonly status: number,
		readonly code: string,
		readonly messageKey: string,
		readonly detail?: string | null,
	) {
		// `detail` goes in the thrown Error's message, which is a log line. It
		// never reaches a screen: the server sends codes, and prose that slipped
		// through would be English in front of an operator reading Chinese.
		super(detail ? `${code}: ${messageKey} (${detail})` : `${code}: ${messageKey}`);
		this.name = "ApiError";
	}

	localized(locale: Locale): string {
		const specific = translate(locale, this.messageKey);
		if (specific !== this.messageKey) return specific;
		return translate(locale, `errors.${this.code}`);
	}

	get isUnauthorized() {
		return this.status === 401;
	}
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
	const response = await fetch(`/api/v1${path}`, {
		...init,
		credentials: "include",
		headers: {
			accept: "application/json",
			...(init.body ? { "content-type": "application/json" } : {}),
			...init.headers,
		},
	});
	if (response.status === 204) return undefined as T;
	const text = await response.text();
	let body: unknown = null;
	try {
		body = text ? JSON.parse(text) : null;
	} catch {
		body = null;
	}
	if (!response.ok) {
		if (body && typeof body === "object") {
			const error = body as { code?: string; message_key?: string; detail?: string };
			throw new ApiError(
				response.status,
				error.code ?? "internal",
				error.message_key ?? "errors.internal",
				error.detail,
			);
		}
		// Not the server's shape: axum's own rejections — a body that did not
		// parse, a header it could not read — are plain text with a 4xx. That
		// is still the request's fault, and the text says what was wrong with
		// it, which "something went wrong on the server" would hide.
		const client = response.status >= 400 && response.status < 500;
		throw new ApiError(
			response.status,
			client ? "bad_request" : "internal",
			client ? "errors.bad_request" : "errors.internal",
			text.trim() || null,
		);
	}
	return body as T;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
	request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) });

export function query(params: object): string {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "") continue;
		search.set(key, String(value));
	}
	const q = search.toString();
	return q ? `?${q}` : "";
}

// -- shapes -------------------------------------------------------------------

export interface Operator {
	user_id: string;
	email: string;
	name: string | null;
	locale: string;
}

export interface Me {
	user: Operator;
	bootstrap_email: string | null;
	origins: { public: string; app: string };
}

export interface EngineModel {
	engine: string;
	label: string;
	market: "us" | "cn";
	/** What a batch planned now would be sampled with. */
	model: string;
	default_model: string;
	override: string | null;
}

export interface CatalogueModel {
	id: string;
	name: string;
	provider: string;
	online: number;
	category: ModelCategory;
}

export type ModelCategory = "text" | "image" | "video" | "audio";

export interface AvailableModels {
	categories: ModelCategory[];
	models: CatalogueModel[];
	/** Curated ids per category, in display order. A missing or empty list offers the whole board. */
	selected: Partial<Record<ModelCategory, string[]>>;
	/** Each role's default model, in the console's order. */
	roles: { role: string; category: ModelCategory; model: string }[];
}

export interface Count {
	status: string;
	count: number;
}

export interface Overview {
	week: string;
	users: { total: number; verified: number; admins: number; new_7d: number };
	orgs: { total: number; paid: number; paused: number; new_7d: number };
	entities: { by_status: Count[]; claimed: number };
	pages: Count[];
	submissions_pending: number;
	jobs: { last_24h: Count[]; queued: number; running: number; failed: number };
	samples_24h: { total: number; notices: number; cost_micros: number };
	spend: {
		platform_today_micros: number;
		platform_cap_micros: number;
		cost_30d_micros: number;
		billed_30d_micros: number;
	};
	agents: { enabled: number; running: number };
	config: {
		paygate: boolean;
		platform_key: boolean;
		mail: boolean;
		google: boolean;
		github: boolean;
		revalidate: boolean;
		public_origin: string;
		app_origin: string;
		api_origin: string;
		gateway_url: string;
		max_concurrent: number;
	};
}

export interface Citations {
	week: string;
	weeks: string[];
	engines_passing: number;
	engines_needed: number;
	gate: { engine: string; label: string; passing: number; questions: number; rate: number }[];
	raw: { engine: string; label: string; cited: number; questions: number }[];
	by_kind: { kind: string; count: number }[];
}

export interface Usage {
	days: number;
	by_day: { day: string; calls: number; cost_micros: number; billed_micros: number }[];
	by_model: {
		model: string;
		calls: number;
		input_tokens: number;
		output_tokens: number;
		cost_micros: number;
		billed_micros: number;
	}[];
	by_kind: { kind: string; calls: number; cost_micros: number }[];
	top_orgs: { org_id: string; name: string; calls: number; billed_micros: number }[];
}

export interface User {
	id: string;
	email: string;
	name: string | null;
	locale: string;
	is_admin: boolean;
	has_password: boolean;
	email_verified_at: string | null;
	created_at: string;
	orgs: number;
	sessions: number;
	providers: string[];
}

export interface UserDetail {
	user: User;
	orgs: {
		id: string;
		name: string;
		slug: string;
		plan_key: string;
		role: string;
		paused: boolean;
		credits: number;
		subscription_status: string | null;
		subscription_ends_at: string | null;
	}[];
	identities: { provider: string; email: string | null; created_at: string }[];
	admin_sessions: number;
	actions: AuditRef[];
}

interface AuditRef {
	id: number;
	org_id?: string | null;
	actor?: string | null;
	action: string;
	target: string | null;
	created_at: string;
}

export interface Org {
	id: string;
	name: string;
	slug: string;
	kind: string;
	plan_key: string;
	paused: boolean;
	onboarded_at: string | null;
	created_at: string;
	entities: number;
	members: number;
	credits: number;
	subscription_status: string | null;
	subscription_ends_at: string | null;
	spent_30d_micros: number;
}

export interface LedgerEntry {
	id: string;
	delta: number;
	balance_after: number;
	reason: string;
	ref_kind: string | null;
	ref_id: string | null;
	created_at: string;
}

export interface Subscription {
	id: string;
	plan_key: string;
	pending_plan_key: string | null;
	status: string;
	interval: string;
	paygate_subscription_id: string | null;
	current_period_start: string | null;
	current_period_end: string | null;
	cancel_at_period_end: boolean;
	created_at: string;
}

export interface OrgDetail {
	org: Org;
	subscription_state: "none" | "active" | "expired";
	members: { user_id: string; email: string; name: string | null; role: string; since: string }[];
	entities: { id: string; name: string; slug: string; status: string; paused: boolean; published_pages: number }[];
	subscriptions: Subscription[];
	credits: { balance: number; permanent: number; expiring: number; expires_at: string | null };
	ledger: LedgerEntry[];
	usage_30d: { model: string; calls: number; billed_micros: number }[];
	jobs: { id: string; kind: string; status: string; last_error: string | null; created_at: string }[];
	actions: AuditRef[];
}

export interface Entity {
	id: string;
	slug: string;
	name: string;
	website: string | null;
	domains: string[];
	status: string;
	paused: boolean;
	source: string | null;
	industry_id: string | null;
	owner_org_id: string | null;
	owner_name: string | null;
	claimed_at: string | null;
	datapoint_count: number;
	published_pages: number;
	created_at: string;
	updated_at: string;
}

export interface EntityDetail {
	entity: Entity;
	aliases: string[];
	tagline_en: string | null;
	tagline_zh: string | null;
	description: string | null;
	/** Slugs from the category tree, in the order chosen. */
	categories: string[];
	datapoints: Record<string, { value?: unknown; unit?: string | null; source_url?: string | null }>;
	logo_url: string | null;
	source_ref: string | null;
	pages: { id: string; kind: string; slug: string; locale: string; status: string; published_at: string | null }[];
	claims: {
		id: string;
		org_id: string;
		domain: string;
		status: string;
		method: string | null;
		attempts: number;
		created_at: string;
	}[];
	measurement: { question_sets: number; citations_30d: number; last_cited_at: string | null };
	submissions: { id: string; kind: string; status: string; created_at: string }[];
}

export interface Submission {
	id: string;
	entity_id: string | null;
	entity_name: string | null;
	kind: string;
	payload: { name?: string; website?: string; host?: string; tagline?: string | null; category?: string | null };
	submitter_email: string | null;
	status: string;
	reviewed_by: string | null;
	reviewed_at: string | null;
	note: string | null;
	created_at: string;
}

export interface Page {
	id: string;
	kind: string;
	slug: string;
	locale: string;
	title: string;
	status: string;
	recipe: string | null;
	generated_by: string | null;
	revision: number;
	owner_org_id: string | null;
	owner_name: string | null;
	entity_names: string[];
	published_at: string | null;
	created_at: string;
	updated_at: string;
}

export interface PageDetail {
	page: Page;
	url: string;
	description: string | null;
	blocks: unknown[];
	jsonld: unknown;
	review_state: Record<string, unknown>;
	entity_ids: string[];
	revisions: { revision: number; author: string | null; note: string | null; created_at: string }[];
	citations: number;
}

export interface Job {
	id: string;
	kind: string;
	key: string;
	status: string;
	attempts: number;
	max_attempts: number;
	org_id: string | null;
	org_name: string | null;
	entity_id: string | null;
	entity_name: string | null;
	last_error: string | null;
	lane: string;
	locked_by: string | null;
	run_after: string;
	created_at: string;
	updated_at: string;
}

export interface JobDetail {
	job: Job;
	payload: unknown;
	result: unknown;
	events: { at: string; level: string; message: string }[];
}

export interface Plan {
	key: string;
	name_en: string;
	name_zh: string;
	monthly_credits: number;
	entities_max: number;
	engines_max: number;
	seats_max: number;
	sample_interval_days: number;
	replication: number;
	cname: boolean;
	agent: boolean;
	workflows_max: number;
	advanced_insights: boolean;
	trial: boolean;
	questions_max: number;
	pages_per_month: number;
	review_first_n: number;
	review_always: boolean;
	price_usd_micros: number;
	price_cny_micros: number;
	price_usd_yearly_micros: number;
	price_cny_yearly_micros: number;
	price_id_usd: string | null;
	price_id_cny: string | null;
	price_id_usd_yearly: string | null;
	price_id_cny_yearly: string | null;
	contact_only: boolean;
	position: number;
	active: boolean;
}

export interface Pack {
	key: string;
	credits: number;
	price_usd_micros: number;
	price_cny_micros: number;
	price_id_usd: string | null;
	price_id_cny: string | null;
	position: number;
	active: boolean;
}

export interface Industry {
	id: string;
	slug: string;
	name_en: string;
	name_zh: string;
	locales: string[];
	entities: number;
}

export interface Category {
	id: string;
	slug: string;
	parent_id: string | null;
	industry_id: string | null;
	name_en: string;
	name_zh: string;
	entities: number;
}

export interface AuditEntry {
	id: number;
	actor_user_id: string | null;
	actor_email: string | null;
	org_id: string | null;
	org_name: string | null;
	action: string;
	target: string | null;
	detail: unknown;
	created_at: string;
}

// -- calls --------------------------------------------------------------------

export interface ModelCall {
	id: string;
	org_id: string;
	entity_id: string;
	request_id: string;
	model: string;
	shape: "message" | "stream" | "embedding";
	meter_kind: string;
	status: "uncertain" | "received";
	reservation: number;
	credits: number;
	error_code: string | null;
	updated_at: string;
}
export const api = {
	modelCalls: (offset = 0) => get<{ calls: ModelCall[]; total: number }>(`/model-calls?offset=${offset}`),
	modelCall: (id: string) => get<unknown>(`/model-calls/${encodeURIComponent(id)}`),
	reconcileModelCall: (id: string, body: unknown) =>
		post<unknown>(`/model-calls/${encodeURIComponent(id)}/reconcile`, body),
	login: (email: string, password: string) => post<{ user: Operator }>("/auth/login", { email, password }),
	logout: () => post<{ ok: true }>("/auth/logout"),
	me: () => get<Me>("/me"),

	overview: () => get<Overview>("/overview"),
	citations: (week?: string) => get<Citations>(`/citations${query({ week })}`),
	usage: () => get<Usage>("/usage"),

	users: (params: object) => get<{ users: User[]; total: number }>(`/users${query(params)}`),
	user: (id: string) => get<UserDetail>(`/users/${id}`),
	setAdmin: (id: string, is_admin: boolean) => post<{ is_admin: boolean }>(`/users/${id}/admin`, { is_admin }),
	verifyUser: (id: string) => post<{ verified: boolean }>(`/users/${id}/verify`),
	revokeSessions: (id: string) => post<{ panel: number; console: number }>(`/users/${id}/sessions/revoke`),
	setPassword: (id: string, password: string) =>
		post<{ ok: true; sessions_ended: number }>(`/users/${id}/password`, { password }),

	orgs: (params: object) => get<{ orgs: Org[]; total: number }>(`/orgs${query(params)}`),
	org: (id: string) => get<OrgDetail>(`/orgs/${id}`),
	updateOrg: (id: string, body: object) => patch<{ org: Org }>(`/orgs/${id}`, body),
	pauseOrg: (id: string, paused: boolean) => post<{ paused: boolean }>(`/orgs/${id}/pause`, { paused }),
	grant: (id: string, body: { credits: number; note?: string; expires_at?: string | null }) =>
		post<{ balance: number }>(`/orgs/${id}/credits/grant`, body),
	revoke: (id: string, body: { credits: number; note?: string }) =>
		post<{ balance: number }>(`/orgs/${id}/credits/revoke`, body),
	setPlan: (id: string, body: { plan_key: string; months?: number; note?: string }) =>
		post<{ plan_key: string; subscription_id: string | null; period_end: string | null; credits_granted: number }>(
			`/orgs/${id}/plan`,
			body,
		),
	endSubscription: (id: string, note?: string) => post<{ ended: string }>(`/orgs/${id}/subscription/end`, { note }),

	entities: (params: object) => get<{ entities: Entity[]; total: number }>(`/entities${query(params)}`),
	entity: (id: string) => get<EntityDetail>(`/entities/${id}`),
	updateEntity: (id: string, body: object) => patch<{ entity: Entity }>(`/entities/${id}`, body),
	releaseEntity: (id: string, note?: string) =>
		post<{ released: boolean; drafts_dropped: number }>(`/entities/${id}/release`, { note }),
	submissions: (params: object) => get<{ submissions: Submission[]; counts: Count[] }>(`/submissions${query(params)}`),
	decide: (id: string, status: string, note?: string) =>
		post<{ status: string; entity_id: string | null }>(`/submissions/${id}/decide`, { status, note }),

	pages: (params: object) => get<{ pages: Page[]; counts: Count[] }>(`/pages${query(params)}`),
	page: (id: string) => get<PageDetail>(`/pages/${id}`),
	publishPage: (id: string) => post<{ status: string }>(`/pages/${id}/publish`),
	unpublishPage: (id: string, reason?: string) => post<{ status: string }>(`/pages/${id}/unpublish`, { reason }),

	jobs: (params: object) =>
		get<{ jobs: Job[]; counts: { kind: string; status: string; count: number }[] }>(`/jobs${query(params)}`),
	job: (id: string) => get<JobDetail>(`/jobs/${id}`),
	retryJob: (id: string) => post<{ status: string }>(`/jobs/${id}/retry`),
	cancelJob: (id: string) => post<{ status: string }>(`/jobs/${id}/cancel`),

	plans: () => get<{ plans: Plan[]; orgs_on: { plan_key: string; count: number }[] }>("/plans"),
	putPlan: (key: string, plan: Plan) => put<{ plan: Plan }>(`/plans/${key}`, plan),
	markup: () => get<{ credit_markup_bp: number }>("/markup"),
	putMarkup: (body: { credit_markup_bp: number }) => put<{ credit_markup_bp: number }>("/markup", body),
	packs: () => get<{ packs: Pack[] }>("/packs"),
	putPack: (key: string, body: Omit<Pack, "key">) => put<{ packs: Pack[] }>(`/packs/${key}`, body),
	newPack: (body: Pack) => post<{ packs: Pack[] }>("/packs", body),
	industries: () => get<{ industries: Industry[]; categories: Category[] }>("/industries"),

	engines: () => get<{ engines: EngineModel[] }>("/engines"),
	putEngines: (models: Record<string, string>) => put<{ engines: EngineModel[] }>("/engines", { models }),

	availableModels: () => get<AvailableModels>("/models"),
	putAvailableModels: (selected: Partial<Record<ModelCategory, string[]>>) =>
		put<AvailableModels>("/models", { selected }),
	putDefaultModels: (defaults: Record<string, string>) => put<AvailableModels>("/models/defaults", { defaults }),

	audit: (params: object) => get<{ entries: AuditEntry[] }>(`/audit${query(params)}`),
};
